import { makeAutoObservable, runInAction } from 'mobx';
import { Notification } from 'ksnm-common-ui/lib/Notification';
import type { SelectRows } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi, fetchWithAudit } from '@src/api';
import type { CmApi, CompanyReportDto, ControlVersionWithNameDto, UncreatedControlVersionDto } from '@src/api';

import { preloaderStore } from '../preloaderStore';

interface ISelected {
  selectRows: SelectRows;
  rows: ResponseDataRow[];
}

export interface ControlVersionWithNameDtoWitId extends ControlVersionWithNameDto {
  id: string;
  name?: string;
}

class PassingControlStore {
  controlReports: CompanyReportDto[] = [];

  uncreatedControlVersions: ControlVersionWithNameDtoWitId[] = [];

  selected: ISelected | null = null;

  api: CmApi;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  setSelected = (value) => {
    this.selected = value;
  };

  getControlReportByOrganizationCode = async (organizationCode: string): Promise<void> => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.companyReports.getControlReportByOrganizationCode(organizationCode);
      runInAction(() => {
        this.controlReports = data.id ? [data] : [];
      });
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  runControls = async (reportPackageCode: string, ctrlVersionCodes: string[]): Promise<string | undefined> => {
    preloaderStore.preloader(true);

    try {
      const response = await fetchWithAudit(
        this.api.controls.runControls({
          reportPackageCode,
          ctrlVersionCodes,
        }),
        {
          moduleName: 'Calculator',
          serviceName: 'Control_procedure_execution',
          operationName: 'Start_kp_calculation',
          message: 'Запуск расчёта Контрольных процедур',
        },
        200,
      );

      if (response.status === 200) {
        preloaderStore.preloader(false);
        return 'success';
      }
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  getUncreatedControlVersions = async (reportPackageCode: string) => {
    preloaderStore.preloader(true);

    try {
      const { data } = await this.api.uncreatedControls.getUncreatedControlVersions({
        reportPackageCode,
      });

      if (data) {
        const versions = data.map((item) => ({ ...item, name: item.controlName, id: item.controlVersionCode ?? '' }));

        runInAction(() => {
          this.uncreatedControlVersions = versions;
        });

        return versions;
      }
    } catch (error) {
      console.error(error);
    } finally {
      preloaderStore.preloader(false);
    }
  };

  addUncreatedReportsIntoPackage = async (
    uncreatedControlVersion: UncreatedControlVersionDto,
    organizationCode: string,
    close,
  ) => {
    preloaderStore.preloader(true);
    try {
      const response = await this.api.uncreatedControls.addUncreatedControlVersionsIntoPackage(uncreatedControlVersion);

      if (response.status === 200) {
        Notification({
          message: 'Контрольные процедуры добавлены в пакет',
          type: 'success',
        });

        close();
        this.clear(); // для сброса таблицы
        this.getControlReportByOrganizationCode(organizationCode);
      }
    } catch (error) {
      console.error(error);
    } finally {
      preloaderStore.preloader(false);
    }
  };

  clear = () => {
    this.controlReports = [];
    this.selected = null;
    this.uncreatedControlVersions = [];
  };
}

export const passingControlStore = new PassingControlStore({
  api: cmApi,
});
